import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import RegistrationForm from './components/RegistrationForm';


const Login = () => {
  const location = useLocation();
  const locationState = location?.state || {};
  const [isRegistering, setIsRegistering] = useState(locationState?.showRegister || false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const { signIn, signUp, getRedirectPath } = useAuth();

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData?.email || !formData?.password) {
      setError('Please fill in all fields');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setError('');

    try {
      const result = await signIn(formData?.email, formData?.password);
      const profile = result?.profile;
      
      if (profile?.role === 'client' && !profile?.profile_complete) {
        navigate('/client-profile-setup');
      } else {
        const redirectPath = getRedirectPath(profile);
        navigate(redirectPath);
      }
    } catch (err) {
      if (err?.message?.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please try again.');
      } else if (err?.message?.includes('Email not confirmed')) {
        setError('Please verify your email address before signing in.');
      } else {
        setError(err?.message || 'An error occurred during login. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistration = async (registrationData) => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const data = await signUp(
        registrationData?.email,
        registrationData?.password,
        {
          full_name: registrationData?.fullName,
          phone: registrationData?.phone || '',
          role: registrationData?.role || 'client'
        }
      );

      if (!data?.user && !data?.session) {
        setError('Unable to create account. This email may already be registered. Please try signing in or use a different email.');
        return;
      }

      try {
        const { supabase: supabaseClient } = await import('lib/supabase');
        await supabaseClient?.functions?.invoke('send-verification-email', {
          body: {
            email: registrationData?.email,
            fullName: registrationData?.fullName,
            role: registrationData?.role || 'client',
            confirmationUrl: `${window.location?.origin}/email-verification-confirmation`
          }
        });
      } catch (emailErr) {
        console.warn('Branded email failed, Supabase default email was sent:', emailErr?.message);
      }

      navigate('/email-verification-pending', {
        state: { 
          email: registrationData?.email,
          fromClientRegistration: !!locationState?.formData,
          formData: locationState?.formData || null
        }
      });
    } catch (err) {
      if (err?.message?.includes('User already registered') || err?.message?.includes('already been registered')) {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (err?.message?.includes('Password should be at least')) {
        setError('Password must be at least 6 characters long.');
      } else if (err?.message?.includes('invalid')) {
        setError('Invalid email address. Please check and try again.');
      } else if (err?.message?.includes('rate limit') || err?.message?.includes('too many')) {
        setError('Too many signup attempts. Please wait a few minutes and try again.');
      } else if (err?.message?.includes('email')) {
        setError('There was an issue with your email address. Please check it and try again.');
      } else {
        setError(err?.message || 'An error occurred during registration. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setError('');
    setSuccessMessage('');
    setFormData({ email: '', password: '' });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="Scale" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-text-primary mb-2">LawDesk KE</h1>
          <p className="text-text-secondary">Legal Practice Management System</p>
        </div>

        {/* Form Container */}
        <div className="bg-surface rounded-lg shadow-card border border-border p-8">
          {/* Toggle Tabs */}
          <div className="flex mb-6 bg-background rounded-lg p-1">
            <button
              type="button"
              onClick={() => { if (isRegistering) toggleMode(); }}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-smooth ${
                !isRegistering
                  ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => { if (!isRegistering) toggleMode(); }}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-smooth ${
                isRegistering
                  ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Register
            </button>
          </div>

          <h2 className="text-xl font-heading font-semibold text-text-primary mb-6 text-center">
            {isRegistering ? 'Create Your Account' : 'Sign In to Your Account'}
          </h2>

          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 mb-4 text-sm flex items-start">
              <Icon name="CheckCircle" size={20} className="mr-2 flex-shrink-0 mt-0.5" />
              <span>{successMessage}</span>
            </div>
          )}

          {isRegistering ? (
            <RegistrationForm
              onSubmit={handleRegistration}
              isLoading={isLoading}
              error={error}
            />
          ) : (
            <>
              {error && (
                <div className="bg-error-50 border border-error-100 text-error rounded-lg p-3 mb-4 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData?.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                      placeholder="Enter your email"
                      required
                    />
                    <Icon name="Mail" size={20} className="absolute right-3 top-3.5 text-text-secondary" />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData?.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth pr-12"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3.5 text-text-secondary hover:text-text-primary transition-smooth"
                    >
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <span className="ml-2 text-sm text-text-secondary">Remember me</span>
                  </label>
                  <Link
                    to="/forgot-password-request"
                    className="text-sm text-primary hover:text-primary-700 transition-smooth"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <Icon name="Loader2" size={20} className="animate-spin mr-2" />
                      Signing In...
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-text-secondary">
            © {new Date()?.getFullYear()} LawDesk KE. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;